// Browser glyph-metrics adapter for the portable text layout engine.
//
// This module is the only browser-specific code in the sizing path. A server
// side renderer replaces exactly this file (SixLabors.Fonts: TextMeasurer
// .MeasureBounds is the ink-bounds analogue of actualBoundingBox*, MeasureAdvance
// is the advance-width analogue) and leaves textLayoutEngine.js untouched.
//
// Contract
//   measure(text, font)           -> { advance, inkLeft, inkRight, inkAscent, inkDescent }
//   getFontProbeMetrics(font)     -> { xHeightEm, capHeightEm, ascenderHeightEm, descenderDepthEm }
//   isFontReady(font)             -> boolean
//   loadFont(font)                -> Promise<boolean>
//   getFontKey(font)              -> string
//
// `font` is { fontFamily, fontStyle, fontWeight, fontSizePx }. Every returned
// distance is in pixels at `fontSizePx`, measured from the pen position on the
// alphabetic baseline: `inkLeft` is positive when ink reaches left of the pen,
// `inkAscent` is positive above the baseline, `inkDescent` positive below it.
// Ink metrics scale linearly with font size, so everything is measured once at
// REFERENCE_FONT_SIZE and scaled, which is what makes the cache size agnostic.

const REFERENCE_FONT_SIZE = 100;

const FALLBACK_ASCENT_EM = 0.8;
const FALLBACK_DESCENT_EM = 0.2;

const CAP_HEIGHT_PROBE = 'H';
const X_HEIGHT_PROBE = 'x';
const ASCENDER_PROBE = 'bdfhkl';
const DESCENDER_PROBE = 'gjpqy';
const LINE_SPACING_PROBE = 'Hxgy';

// leading applied when a face reports no usable font bounding box, so the
// fallback still separates consecutive lines rather than letting ink collide
const FALLBACK_LEADING_RATIO = 1.15;

const ZERO_METRICS = {
  advance: 0,
  inkLeft: 0,
  inkRight: 0,
  inkAscent: 0,
  inkDescent: 0
};

const metricsCache = new Map();
const probeCache = new Map();
const readyFontKeys = new Set();

let sharedContext = null;
let hasResolvedSharedContext = false;

const getSharedContext = () => {
  if (hasResolvedSharedContext) {
    return sharedContext;
  }
  hasResolvedSharedContext = true;

  if (typeof document !== 'undefined' && document.createElement) {
    const canvasElement = document.createElement('canvas');
    canvasElement.width = 1;
    canvasElement.height = 1;
    sharedContext = canvasElement.getContext('2d');
  }

  return sharedContext;
};

const normalizeFont = (font = {}) => {
  const fontSizePx = Number(font.fontSizePx);

  return {
    fontFamily: font.fontFamily || 'sans-serif',
    fontStyle: font.fontStyle || 'normal',
    fontWeight: font.fontWeight || 'normal',
    fontSizePx: fontSizePx > 0 ? fontSizePx : REFERENCE_FONT_SIZE
  };
};

const getFontKey = (font) => {
  const { fontFamily, fontStyle, fontWeight } = normalizeFont(font);
  return `${fontFamily}|${fontWeight}|${fontStyle}`;
};

const getFontSpec = (font, fontSizePx = REFERENCE_FONT_SIZE) => {
  const { fontFamily, fontStyle, fontWeight } = normalizeFont(font);
  return `${fontStyle} ${fontWeight} ${fontSizePx}px "${fontFamily}"`;
};

const isFontReady = (font) => {
  const fontKey = getFontKey(font);
  if (readyFontKeys.has(fontKey)) {
    return true;
  }

  if (typeof document === 'undefined' || !document.fonts?.check) {
    return true;
  }

  let ready = false;
  try {
    ready = document.fonts.check(getFontSpec(font));
  } catch (err) {
    ready = true;
  }

  if (ready) {
    readyFontKeys.add(fontKey);
  }

  return ready;
};

const loadFont = (font) => {
  if (isFontReady(font)) {
    return Promise.resolve(true);
  }

  if (typeof document === 'undefined' || !document.fonts?.load) {
    return Promise.resolve(true);
  }

  return document.fonts
    .load(getFontSpec(font))
    .then(() => isFontReady(font))
    .catch(() => false);
};

const measureAtReferenceSize = (text, font) => {
  const context = getSharedContext();
  if (!context) {
    return ZERO_METRICS;
  }

  context.font = getFontSpec(font);
  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';

  const metrics = context.measureText(text);
  const advance = metrics.width || 0;
  const hasInkBounds = typeof metrics.actualBoundingBoxAscent === 'number';

  if (!hasInkBounds) {
    return {
      advance,
      inkLeft: 0,
      inkRight: advance,
      inkAscent: REFERENCE_FONT_SIZE * FALLBACK_ASCENT_EM,
      inkDescent: REFERENCE_FONT_SIZE * FALLBACK_DESCENT_EM,
      fontAscent: 0,
      fontDescent: 0
    };
  }

  return {
    advance,
    inkLeft: metrics.actualBoundingBoxLeft || 0,
    inkRight: metrics.actualBoundingBoxRight || 0,
    inkAscent: metrics.actualBoundingBoxAscent || 0,
    inkDescent: metrics.actualBoundingBoxDescent || 0,
    // the face's own ascent / descent rather than this string's ink; the
    // SixLabors analogue is VerticalMetrics.Ascender / .Descender over
    // UnitsPerEm, which is what keeps line spacing agreeing across renderers
    fontAscent: metrics.fontBoundingBoxAscent || 0,
    fontDescent: metrics.fontBoundingBoxDescent || 0
  };
};

const getReferenceMetrics = (text, font) => {
  const cacheKey = `${getFontKey(font)}|${text}`;
  const cached = metricsCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const measured = measureAtReferenceSize(text, font);

  // metrics taken before an @font-face resolves describe the fallback face, so
  // they are used once but never cached
  if (isFontReady(font)) {
    metricsCache.set(cacheKey, measured);
  }

  return measured;
};

const measure = (text, font) => {
  const normalizedFont = normalizeFont(font);
  const reference = getReferenceMetrics(
    typeof text === 'string' ? text : '',
    normalizedFont
  );
  const scale = normalizedFont.fontSizePx / REFERENCE_FONT_SIZE;

  return {
    advance: reference.advance * scale,
    inkLeft: reference.inkLeft * scale,
    inkRight: reference.inkRight * scale,
    inkAscent: reference.inkAscent * scale,
    inkDescent: reference.inkDescent * scale
  };
};

const getFontProbeMetrics = (font) => {
  const fontKey = getFontKey(font);
  const cached = probeCache.get(fontKey);
  if (cached) {
    return cached;
  }

  const normalizedFont = normalizeFont(font);
  const capHeightEm =
    getReferenceMetrics(CAP_HEIGHT_PROBE, normalizedFont).inkAscent /
    REFERENCE_FONT_SIZE;
  const xHeightEm =
    getReferenceMetrics(X_HEIGHT_PROBE, normalizedFont).inkAscent /
    REFERENCE_FONT_SIZE;
  const ascenderHeightEm =
    getReferenceMetrics(ASCENDER_PROBE, normalizedFont).inkAscent /
    REFERENCE_FONT_SIZE;
  const descenderDepthEm =
    getReferenceMetrics(DESCENDER_PROBE, normalizedFont).inkDescent /
    REFERENCE_FONT_SIZE;

  const resolvedCapHeightEm =
    capHeightEm > 0 ? capHeightEm : FALLBACK_ASCENT_EM;
  const resolvedAscenderHeightEm = Math.max(
    ascenderHeightEm,
    resolvedCapHeightEm
  );
  const resolvedDescenderDepthEm = descenderDepthEm > 0 ? descenderDepthEm : 0;

  // line spacing is a property of the face, not of the string, so it is probed
  // once and travels with the layout result. Deriving it from the font's own
  // ascent + descent - rather than inheriting a renderer's default line height
  // - is what lets a different renderer reproduce the same block.
  const spacingMetrics = getReferenceMetrics(
    LINE_SPACING_PROBE,
    normalizedFont
  );
  const fontSpacingEm =
    (spacingMetrics.fontAscent + spacingMetrics.fontDescent) /
    REFERENCE_FONT_SIZE;
  // the fallback must mirror SixLaborsTextMetricsProvider.ResolveLineSpacingEm
  // exactly, descent floor included: an all-caps face has no descender ink, and
  // an ascent-only fallback lands right on a line-count knife edge
  const fallbackDescentEm =
    resolvedDescenderDepthEm > 0
      ? resolvedDescenderDepthEm
      : FALLBACK_DESCENT_EM;
  const lineSpacingEm =
    fontSpacingEm > 0
      ? fontSpacingEm
      : (resolvedAscenderHeightEm + fallbackDescentEm) * FALLBACK_LEADING_RATIO;

  const probe = {
    capHeightEm: resolvedCapHeightEm,
    xHeightEm: xHeightEm > 0 ? xHeightEm : resolvedCapHeightEm / 2,
    ascenderHeightEm: resolvedAscenderHeightEm,
    descenderDepthEm: resolvedDescenderDepthEm,
    lineSpacingEm
  };

  if (isFontReady(normalizedFont)) {
    probeCache.set(fontKey, probe);
  }

  return probe;
};

const textMetricsProvider = {
  measure,
  getFontProbeMetrics,
  isFontReady,
  loadFont,
  getFontKey,
  getFontSpec
};

export default textMetricsProvider;
